import { useMemo } from 'react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';

import { downloadFailedFilesReport, downloadSuccessfulFilesReport } from './report-utils';
import { UploadFailureReason } from '../../../../../../../../backend/src/types/types';
import { useStore } from '../../../../../../store';
import { useGetMatterUploadHistory } from '@/api/queries/useGetMatterUploadHistory';
import { useGetUploadFiles } from '@/api/queries/useGetUploadFiles';
import { UploadFailure } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/new/components/shadcn/table';
import { TableNullField } from '@/new/components/table/table-null-field';
import { TablePagination } from '@/new/components/table/table-pagination';

const fallbackTableData: UploadFailure[] = [];
const columnHelper = createColumnHelper<UploadFailure>();

const formatNumber = (num: number) => {
  return num.toLocaleString();
};

export function UploadFailures() {
  const { matterId, uploadId } = useParams();
  if (!matterId || !uploadId) return null;

  const { data: uploads } = useGetMatterUploadHistory(matterId);
  const upload = uploads?.find((u) => u.id === uploadId);
  const { data: uploadFiles, isLoading: isLoadingFiles } = useGetUploadFiles(matterId, uploadId);

  const { openReferenceDrawer } = useStore();

  const columns = useMemo(
    () => [
      columnHelper.accessor('filename', {
        header: () => <span>Filename</span>,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-800">{info.getValue()}</span>
          </div>
        ),
      }),
      columnHelper.accessor('extension', {
        header: () => <span>Type</span>,
        cell: (info) => {
          const ext = info.getValue();
          return <span className="text-sm text-gray-500">{ext ? ext.toUpperCase() : '—'}</span>;
        },
      }),
      columnHelper.accessor('createdDate', {
        header: () => <span>Upload Date</span>,
        cell: (info) => {
          const date = info.getValue();
          return <span className="text-sm text-gray-500">{date ? formatDate(date, true, true) : '—'}</span>;
        },
      }),
      columnHelper.accessor('rejectedReason', {
        header: () => <span>Rejected Reason</span>,
        cell: (info) => {
          const reason = info.getValue();
          if (!reason) return <TableNullField />;

          return reason === UploadFailureReason.Duplicate ? (
            <div>
              Duplicate{' '}
              {info.row.original.duplicateFile && (
                <span>
                  of{' '}
                  {info.row.original.duplicateFile?.doc?.id ? (
                    <button
                      className="font-medium text-blue-500"
                      onClick={() => openReferenceDrawer({ docId: info.row.original.duplicateFile?.doc?.id })}
                    >
                      {info.row.original.duplicateFile?.name}
                    </button>
                  ) : (
                    <span className="font-medium">{info.row.original.duplicateFile?.name}</span>
                  )}
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm capitalize">{reason}</span>
          );
        },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: upload?.uploadFailures ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  if (!upload) {
    return (
      <div className="flex flex-col">
        <div className="mb-4">
          <Button variant="outline" size="sm" asChild className="h-auto px-2 py-1 text-gray-600 hover:text-gray-800">
            <Link to={`/matters/${matterId}/settings/upload-history`}>
              <Icon className="lucide--arrow-left mr-1 h-4 w-4" />
              Back to Upload History
            </Link>
          </Button>
        </div>
        <div className="rounded-md border border-gray-200 bg-white p-8 text-center">
          <p className="text-sm text-gray-500">Upload not found.</p>
        </div>
      </div>
    );
  }

  const successCount = upload.successfullyUploadedFileCount ?? 0;
  const failureCount = upload.uploadFailures?.length ?? 0;

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <Button asChild variant="default" size="sm" className="h-auto px-2 py-1">
          <Link to={`/matters/${matterId}/settings/upload-history`}>
            <Icon className="lucide--arrow-left mr-1 h-4 w-4" />
            Back to Upload History
          </Link>
        </Button>
      </div>

      <h2 className="mb-2 font-serif text-lg font-semibold">Upload Details</h2>

      <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-500">
        <span>
          Uploaded on <span className="text-gray-700">{formatDate(upload.createdAt, true, true)}</span>
        </span>
        <span className="text-gray-300">|</span>
        <div className="flex items-center gap-2">
          <span>
            <span className="font-medium text-green-600">{formatNumber(successCount)}</span> successful
          </span>
          <Button
            variant="default"
            size="sm"
            onClick={() => uploadFiles && downloadSuccessfulFilesReport(uploadFiles, uploadId)}
            disabled={isLoadingFiles || successCount === 0}
            className="ml-1 h-auto px-1.5 py-0.5 text-xs"
          >
            <Icon className="lucide--download h-3 w-3" />
            Download report
          </Button>
        </div>
        <span className="text-gray-300">|</span>
        <div className="flex items-center gap-2">
          <span>
            <span className={`font-medium ${failureCount > 0 ? 'text-red-600' : 'text-gray-600'}`}>
              {formatNumber(failureCount)}
            </span>{' '}
            failed
          </span>
          <Button
            variant="default"
            size="sm"
            onClick={() => upload?.uploadFailures && downloadFailedFilesReport(upload.uploadFailures, uploadId)}
            disabled={failureCount === 0}
            className="ml-1 h-auto px-1.5 py-0.5 text-xs"
          >
            <Icon className="lucide--download h-3 w-3" />
            Download report
          </Button>
        </div>
      </div>

      {failureCount > 0 && (
        <>
          <div className="flex items-center gap-2">
            <Icon className="lucide--alert-triangle h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-700">Failed Files</h3>
          </div>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination table={table} plural="failed uploads" />
        </>
      )}

      {failureCount === 0 && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-6 text-center">
          <Icon className="lucide--check-circle mx-auto mb-2 h-8 w-8 text-green-500" />
          <p className="text-sm text-gray-600">All files uploaded successfully!</p>
        </div>
      )}
    </div>
  );
}
